import { isAllowedWorldZoom, steppedWorldZoom, type RenderDivisor, type Size } from "odyssey-shared";
import { writeZoomStep, type SettingsStore, type ZoomStep } from "./settings.ts";

/**
 * The world camera's zoom (ADR-061, "the default world zoom fits the cap" as amended by "world zoom 2.5 ships
 * where it fits"): the smallest allowed zoom whose view fits the vision cap on this window, a whole number or
 * 2.5, moved by the viewer's step one place through the zooms allowed there, and recomputed whenever the
 * window's device size changes. `?zoom=` on the page is an absolute override for development, which neither the
 * window nor the step moves. At 2.5 the world is drawn at 3 and resampled (`world-ui/sharp-zoom/`). Above render
 * resolution Full (ADR-093 decision 10) the default is a whole zoom of the render size and an override of 2.5
 * is drawn at 3, so every zoom there is whole and the resample never runs.
 */

/**
 * The zoom a camera follows.
 */
export interface WorldZoomSource {
  /** The zoom now. */
  readonly current: number;
  /**
   * Calls back with each new zoom.
   *
   * @param listener Called with the zoom whenever it changes.
   * @returns A function that stops the calls.
   */
  subscribe(listener: (zoom: number) => void): () => void;
}

/**
 * The part of a Phaser camera the zoom drives.
 */
export interface ZoomableCamera {
  setZoom(zoom: number): unknown;
}

/**
 * Reads `?zoom=` as an absolute override.
 *
 * @param raw The parameter's value, or null when the page has none.
 * @returns An allowed zoom (a whole number of at least 1, or 2.5), or null when absent or anything else, such as
 * 2.6, which the world cannot be drawn at with P1.9-T5's guarantees.
 */
export function parseZoomOverride(raw: string | null): number | null {
  if (raw === null || raw.trim() === "") {
    return null;
  }
  const zoom = Number(raw);
  return isAllowedWorldZoom(zoom) ? zoom : null;
}

/**
 * The world zoom for a window.
 *
 * @param device The window's size in device pixels.
 * @param step The viewer's step from the default: Farther, Default or Closer.
 * @param override The page's absolute override, or null.
 * @param divisor The render divisor in force; Full when omitted.
 * @returns The override when there is one, rounded up to a whole zoom above Full; otherwise the window's default
 * at that divisor moved one place by the step through the zooms allowed there (2, 2.5 and 3 at 1920 by 1080 at
 * Full; 1, 2 and 3 at 2560 by 1440 at Half), at least 1, and above Full never a zoom that would show more than
 * Full shows at the same step (`steppedWorldZoom`: 2, 2 and 3 at 2560 by 1496 at Half). The zoom is in render
 * pixels a world pixel.
 */
export function resolveWorldZoom(device: Size, step: ZoomStep, override: number | null, divisor: RenderDivisor = 1): number {
  if (override !== null) {
    return divisor === 1 ? override : Math.ceil(override);
  }
  return steppedWorldZoom(device, divisor, step);
}

/**
 * The page's world zoom: the window's device size, the viewer's step and the override, and whoever follows
 * the result.
 */
export class WorldZoom implements WorldZoomSource {
  private zoom: number;
  private device: Size;
  private stepNow: ZoomStep;
  private divisor: RenderDivisor;
  private readonly listeners = new Set<(zoom: number) => void>();

  /**
   * @param device The window's size in device pixels.
   * @param step The viewer's step, as `readZoomStep` gives it.
   * @param override The page's absolute override, or null.
   * @param store Where a new step is kept; this browser's `localStorage` when omitted.
   * @param divisor The render divisor in force; Full when omitted.
   */
  constructor(
    device: Size,
    step: ZoomStep,
    private readonly override: number | null,
    private readonly store?: SettingsStore | null,
    divisor: RenderDivisor = 1,
  ) {
    this.device = device;
    this.stepNow = step;
    this.divisor = divisor;
    this.zoom = resolveWorldZoom(device, step, override, divisor);
  }

  /**
   * The zoom now.
   *
   * @returns The zoom.
   */
  get current(): number {
    return this.zoom;
  }

  /**
   * The viewer's step now, which Settings shows: kept even while an override decides the zoom.
   *
   * @returns The step.
   */
  get step(): ZoomStep {
    return this.stepNow;
  }

  /**
   * Recomputes the zoom for a new device size or render divisor.
   *
   * @param device The window's size in device pixels.
   * @param divisor The render divisor in force; the one already held when omitted.
   */
  resize(device: Size, divisor: RenderDivisor = this.divisor): void {
    this.device = device;
    this.divisor = divisor;
    this.update();
  }

  /**
   * Takes the viewer's new step, keeps it, and recomputes the zoom.
   *
   * @param step The new step.
   */
  setStep(step: ZoomStep): void {
    this.stepNow = step;
    writeZoomStep(step, this.store);
    this.update();
  }

  /**
   * Calls back with each new zoom.
   *
   * @param listener Called with the zoom whenever it changes.
   * @returns A function that stops the calls.
   */
  subscribe(listener: (zoom: number) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Recomputes the zoom and tells the listeners when it moved.
   */
  private update(): void {
    const zoom = resolveWorldZoom(this.device, this.stepNow, this.override, this.divisor);
    if (zoom === this.zoom) {
      return;
    }
    this.zoom = zoom;
    for (const listener of this.listeners) {
      listener(zoom);
    }
  }
}

/**
 * What of a Phaser camera decides where its view matrix puts the world: its size in device pixels, its
 * origin as a fraction of that size, its zoom and its scroll in world pixels.
 */
export interface GridCamera {
  readonly width: number;
  readonly height: number;
  readonly originX: number;
  readonly originY: number;
  readonly zoom: number;
  readonly scrollX: number;
  readonly scrollY: number;
}

/**
 * The scroll on one axis near the given one whose view translation is a whole device pixel, rounded against the
 * whole device pixel nearest the camera's origin point (the one before it when the point is mid-pixel) rather than
 * the origin point itself. At a zoom that puts
 * a world pixel on a half device pixel (2.5), an odd size puts the origin point mid-pixel on that axis, and rounding
 * against it would resolve a followed point's half-pixel ties on the frames the other axis is whole: a diagonal walk
 * would move the world 3 and 2 device pixels where it should move 3 and 3, a one-pixel jog across the walk at the
 * walking rate. Measured from the same whole-pixel phase on both axes, equal steps of the followed point move both
 * axes equally.
 *
 * @param size The camera's width or height in device pixels.
 * @param origin The camera's origin on that axis, as a fraction of the size.
 * @param zoom The camera's zoom.
 * @param scroll The scroll on that axis, in world pixels.
 * @returns The scroll, moved by at most half a device pixel where the origin point is a whole device pixel and by
 *   at most one where it is not, for any origin.
 */
function snapAxis(size: number, origin: number, zoom: number, scroll: number): number {
  const centre = size * origin;
  const phase = centre - Math.ceil(centre - 0.5);
  const pivot = centre * (1 - zoom);
  const translation = Math.round(pivot - zoom * scroll - phase);
  return (pivot - translation) / zoom;
}

/**
 * Puts the camera's world on the device pixel grid. Phaser 4.2.1's `Camera.preRender` translates the world
 * by `size * origin * (1 - zoom) - zoom * scroll` on each axis and does not round a scaled quad, so at an odd
 * canvas size and an even zoom a whole scroll still leaves every world pixel edge half a device pixel off
 * the grid and texels come out uneven. This moves each scroll so that the translation is whole, by at most half a
 * device pixel on an axis whose origin point is a whole device pixel and by up to one on an axis where it is not
 * (an odd size at origin 0.5), so that both axes round from the same phase and a followed diagonal walk moves the
 * world equally on both. A scroll the camera's bounds then clamp to a world edge is whole on its own.
 *
 * @param camera The camera after it has been centred for the frame.
 * @returns The scroll to set.
 */
export function snapScrollToDevicePixels(camera: GridCamera): { readonly scrollX: number; readonly scrollY: number } {
  return {
    scrollX: snapAxis(camera.width, camera.originX, camera.zoom, camera.scrollX),
    scrollY: snapAxis(camera.height, camera.originY, camera.zoom, camera.scrollY),
  };
}

/**
 * Sets a camera to a zoom source's zoom and keeps it there.
 *
 * @param camera The world camera.
 * @param source The zoom to follow.
 * @returns A function that stops following.
 */
export function followWorldZoom(camera: ZoomableCamera, source: WorldZoomSource): () => void {
  camera.setZoom(source.current);
  return source.subscribe((zoom) => {
    camera.setZoom(zoom);
  });
}
